import React, { useState, useEffect } from 'react';

// Util
import { send_request } from '../../../../utils/Request';

// MUI
import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
} from '@mui/material';
import { runLoading } from '../../../../utils/showLoadingScreen';
import MappingTable from './MappingTable';
import { CheckCircle, ErrorRounded, Refresh } from '@mui/icons-material';
import {
  BrokenPlaceholder,
  CenterGrid,
  Right,
  StyledAlert,
  TopText,
} from './RenameStyledComponents';
import PlaceholderChip from '../../../common/PlaceholderChip';

function FixBrokenPlaceholders({
  projectId,
  closeDialog,
  onBack,
  openInPane,
  forceCloseComponentPanes,
}) {
  const [isLoading, setIsLoading] = useState(true);
  const [isChanging, setIsChanging] = useState(false);
  const [showResultsPage, setShowResultsPage] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const [brokenPHs, setBrokenPHs] = useState(null);
  const [availablePHs, setAvailablePHs] = useState(null);
  const [values, setValues] = useState(null);

  useEffect(() => {
    if (showResultsPage) return;
    setIsLoading(true);

    let fetchBrokenPHs = getBrokenPHs();
    let fetchAvailablePHs = getAvailablePHs();

    Promise.all([fetchBrokenPHs, fetchAvailablePHs]).then(res => {
      let broken = res[0];
      let available = res[1];

      if (!broken || broken.data === null) setBrokenPHs([]);
      else setBrokenPHs(broken.data);

      if (!available || available.data === null) setAvailablePHs([]);
      else setAvailablePHs(getPHList(available.data));

      setIsLoading(false);
    });
  }, [showResultsPage, refreshTrigger]);

  const getBrokenPHs = async () => {
    let url = `project-service/placeholder/get_broken_placeholders/${projectId}`;
    let fetchBrokenPHs = await send_request(url, null, null, 'GET');
    return fetchBrokenPHs;
  };

  const getAvailablePHs = async () => {
    let url = `component-service/placeholder/${projectId}`;
    let fetchAvailablePHs = await send_request(url, null, null, 'GET');
    return fetchAvailablePHs;
  };

  const getPHList = components => {
    let res = [];

    for (let i = 0; i < components.length; i++) {
      let pHs = components[i].placeholders;
      if (!pHs) continue;
      res = [...res, ...pHs];
    }

    return res;
  };

  const getLoader = () => {
    return (
      <DialogContent>
        <Grid container marginBottom={2}>
          <Grid xs={12} direction={'column'} alignItems={'center'} container item>
            <Grid item>{runLoading()}</Grid>
            <p style={{ textAlign: 'center', whiteSpace: 'pre-line' }}>
              Checking workflow for issues...
            </p>
          </Grid>
        </Grid>
      </DialogContent>
    );
  };

  const handleChangeSelected = async () => {
    setIsChanging(true);

    // Force close any open component panes before making changes
    // This prevents the panes from overwriting our placeholder changes when they're closed later
    if (forceCloseComponentPanes) {
      forceCloseComponentPanes();
      // Wait a small amount to ensure the pane close operation completes
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    let data = [];
    for (let i = 0; i < brokenPHs.length; i++) {
      let val = values[i];
      if (!val || val.key === 'Ignore/Do not change') continue;
      data.push({
        oldKey: brokenPHs[i].key,
        newKey: val.key,
      });
    }

    let url = `project-service/placeholder/replace_used_placeholders/${projectId}`;

    send_request(url, data, null, 'POST')
      .then(() => {
        setShowResultsPage(true);
        setIsChanging(false);
      })
      .catch(e => {
        console.log(e);
        setIsChanging(false);
        handleFixMore();
      });
  };

  const handleFixMore = () => {
    setShowResultsPage(false);
    setValues(null);
    setBrokenPHs(null);
  };

  const handleRefresh = () => {
    setShowResultsPage(false);
    setValues(null);
    setBrokenPHs(null);
    setAvailablePHs(null);
    setRefreshTrigger(prevTrigger => prevTrigger + 1);
  };

  const getPlaceholderChip = item => (
    <PlaceholderChip placeholderKey={item.key} type={item.type} length={item.length} wrapText />
  );

  const getResultsPageContent = () => {
    let anyReplaced =
      values !== null && values.filter(val => val.key !== 'Ignore/Do not change').length > 0;

    const remappedItems = showResultsPage
      ? values
          .map((item, idx) => ({
            item,
            idx,
            brokenPH: brokenPHs[idx],
          }))
          .filter(({ item }) => item.key !== 'Ignore/Do not change')
      : [];

    return (
      <Grid container spacing={3} sx={{ padding: '16px 0' }}>
        <Grid item xs={12}>
          <StyledAlert
            severity={anyReplaced ? 'success' : 'warning'}
            icon={anyReplaced ? <CheckCircle /> : <ErrorRounded />}
            borderColor={anyReplaced ? 'rgba(85, 167, 122, 1)' : 'rgba(255, 167, 34, 1)'}
            bgColor={anyReplaced ? 'rgba(85, 167, 122, 0.1)' : 'rgba(255, 167, 34, 0.1)'}
            sx={{ marginBottom: '16px' }}
          >
            {anyReplaced
              ? `Successfully remapped ${remappedItems.length} broken placeholder${
                  remappedItems.length !== 1 ? 's' : ''
                }`
              : 'No placeholders have been remapped'}
          </StyledAlert>
        </Grid>

        {anyReplaced && (
          <Grid item xs={12}>
            <TopText
              style={{
                fontWeight: 500,
                marginBottom: '16px',
                display: 'block',
                fontSize: '16px',
              }}
            >
              Placeholder Mappings:
            </TopText>
            <Grid container spacing={2}>
              {remappedItems.map(({ item, idx, brokenPH }, mapIdx) => (
                <Grid item xs={12} key={mapIdx}>
                  <div
                    style={{
                      padding: '16px',
                      backgroundColor: 'rgba(248, 250, 252, 1)',
                      borderRadius: '8px',
                      border: '1px solid rgba(229, 231, 235, 1)',
                      transition: 'all 0.2s ease',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: 'rgba(243, 244, 246, 1)',
                      },
                    }}
                  >
                    <Grid container spacing={2} alignItems="center" sx={{ width: '100%' }}>
                      <Grid item xs={5}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <div
                            style={{
                              fontSize: '12px',
                              color: 'rgba(107, 114, 128, 1)',
                              fontWeight: 500,
                            }}
                          >
                            From (Broken)
                          </div>
                          <BrokenPlaceholder
                            style={{
                              fontSize: '13px',
                              padding: '8px 12px',
                              maxWidth: '100%',
                              wordBreak: 'break-word',
                              overflowWrap: 'break-word',
                              whiteSpace: 'normal',
                            }}
                          >
                            {`$\{${brokenPH.key}\}`}
                          </BrokenPlaceholder>
                        </div>
                      </Grid>
                      <Grid item xs={2} sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Right
                          sx={{
                            color: 'rgba(34, 197, 94, 1)',
                            fontSize: '24px',
                            fontWeight: 'bold',
                          }}
                        />
                      </Grid>
                      <Grid item xs={5}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <div
                            style={{
                              fontSize: '12px',
                              color: 'rgba(107, 114, 128, 1)',
                              fontWeight: 500,
                            }}
                          >
                            To (Fixed)
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              maxWidth: '100%',
                              overflow: 'hidden',
                            }}
                          >
                            <div
                              style={{
                                maxWidth: '100%',
                                overflow: 'hidden',
                              }}
                            >
                              {getPlaceholderChip(item)}
                            </div>
                          </div>
                        </div>
                      </Grid>
                    </Grid>
                  </div>
                </Grid>
              ))}
            </Grid>
          </Grid>
        )}

        {!anyReplaced && (
          <Grid item xs={12}>
            <div
              style={{
                textAlign: 'center',
                padding: '24px',
                color: 'rgba(107, 114, 128, 1)',
                fontSize: '14px',
                backgroundColor: 'rgba(249, 250, 251, 1)',
                borderRadius: '8px',
                border: '1px solid rgba(229, 231, 235, 1)',
              }}
            >
              <div style={{ marginBottom: '8px', fontWeight: 500 }}>
                No changes were made to placeholders.
              </div>
              <div>Use "Fix More" to make additional changes.</div>
            </div>
          </Grid>
        )}
      </Grid>
    );
  };

  const getFixBrokenPlaceholdersContent = () => {
    return (
      <Grid container marginTop={2}>
        <Grid item xs={12}>
          {brokenPHs && brokenPHs.length > 0 ? (
            <MappingTable
              brokenPHs={brokenPHs}
              availablePHs={availablePHs}
              isChanging={isChanging}
              setIsChanging={setIsChanging}
              values={values}
              setValues={setValues}
              projectId={projectId}
              closeDialog={closeDialog}
              openInPane={openInPane}
            />
          ) : (
            <Grid container marginTop={3}>
              <Grid item xs={12}>
                <StyledAlert
                  severity={'warning'}
                  icon={<ErrorRounded />}
                  borderColor={'rgba(255, 167, 34, 1)'}
                  bgColor={'rgba(255, 167, 34, 0.1)'}
                >
                  <Grid container>
                    <Grid item>No broken placeholders found</Grid>
                  </Grid>
                </StyledAlert>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>
    );
  };

  const getContent = () => {
    // Check if all broken placeholders are set to ignore
    const allIgnored =
      brokenPHs &&
      values &&
      brokenPHs.length > 0 &&
      brokenPHs.every((_, index) => {
        const val = values[index];
        return !val || val.key === 'Ignore/Do not change';
      });

    return (
      <>
        <DialogTitle>Fix broken placeholders</DialogTitle>
        <DialogContent>
          {!showResultsPage ? getFixBrokenPlaceholdersContent() : getResultsPageContent()}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={onBack} disabled={isChanging}>
            Back
          </Button>
          {!showResultsPage ? (
            <>
              <Button
                variant="outlined"
                onClick={handleRefresh}
                disabled={isChanging}
                startIcon={<Refresh />}
              >
                Refresh
              </Button>
              <Button variant="outlined" onClick={closeDialog} disabled={isChanging}>
                Close
              </Button>
              <Button
                variant="contained"
                onClick={handleChangeSelected}
                disabled={isChanging || !brokenPHs || brokenPHs.length <= 0 || allIgnored}
              >
                Change Selected
                {isChanging && <CircularProgress size={24} className="buttonProgress" />}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outlined"
                onClick={handleRefresh}
                disabled={isChanging}
                startIcon={<Refresh />}
              >
                Refresh
              </Button>
              <Button variant="outlined" onClick={handleFixMore} sx={{ marginLeft: 'auto' }}>
                Fix More
              </Button>
              <Button variant="contained" onClick={closeDialog} disabled={isChanging}>
                Done
              </Button>
            </>
          )}
        </DialogActions>
      </>
    );
  };

  return !isLoading ? getContent() : getLoader();
}

export default FixBrokenPlaceholders;
